import React from 'react';
import { paymentCatalogAmount } from '../../utils/formatMoney';

const BookingReceipt = ({ booking, payments, vendor, service, packageData }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generateReceiptHTML = () => {
    const completedPayments = payments.filter(p => p.status === 'completed');
    const totalPaid = completedPayments.reduce((sum, p) => sum + paymentCatalogAmount(p), 0);
    
    // Helper function to safely parse numeric values (handles strings from database)
    const getNumeric = (val) => {
      if (val === null || val === undefined || val === '') return 0;
      const strVal = String(val).trim();
      if (strVal === '' || strVal === 'null' || strVal === 'undefined' || strVal === 'NaN') {
        return 0;
      }
      const cleaned = strVal.replace(/[^0-9.-]/g, '');
      if (cleaned === '' || cleaned === '-' || cleaned === '.') {
        return 0;
      }
      const parsed = parseFloat(cleaned);
      if (isNaN(parsed) || !isFinite(parsed)) {
        return 0;
      }
      return parsed;
    };
    
    // Calculate amounts using new payment structure
    const subtotal = getNumeric(booking.subtotal_amount || 0);
    const platformFee = getNumeric(booking.platform_fee || 0);
    const totalAmount = subtotal + platformFee; // Client pays subtotal + platform fee
    const addonRows = (booking.booking_addons || booking.bookingAddons || []).filter(
      (addon) => addon && (addon.addon_name || addon.name)
    );
    const addonHtml = addonRows.map((addon) => {
      const name = addon.addon_name || addon.name;
      const qty = Math.max(1, Number(addon.quantity) || 1);
      const amount = addon.total_addon_price != null && addon.total_addon_price !== ''
        ? getNumeric(addon.total_addon_price)
        : getNumeric(addon.addon_price) * qty;
      const qtyLabel = qty > 1 ? ` × ${qty}` : '';
      return `<div class="row"><span class="label">Add-on: ${name}${qtyLabel}</span><span class="value">$${amount.toFixed(2)}</span></div>`;
    }).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${booking.booking_reference}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
          .receipt-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .booking-ref { font-size: 14px; color: #666; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .label { font-weight: 600; }
          .value { text-align: right; }
          .payment-item { margin-bottom: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
          .total { font-size: 18px; font-weight: bold; margin-top: 20px; padding-top: 15px; border-top: 2px solid #000; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="receipt-title">PAYMENT RECEIPT</div>
          <div class="booking-ref">Booking #${booking.booking_reference}</div>
          <div style="margin-top: 10px; font-size: 12px;">Date: ${formatDate(booking.created_at)}</div>
        </div>

        <div class="section">
          <div class="section-title">Booking Details</div>
          <div class="row"><span class="label">Event Date:</span><span class="value">${formatDate(booking.event_date)}</span></div>
          <div class="row"><span class="label">Service:</span><span class="value">${service?.service_name || 'N/A'}</span></div>
          ${packageData ? `<div class="row"><span class="label">Package:</span><span class="value">${packageData.package_name}</span></div>` : ''}
          ${addonHtml}
          <div class="row"><span class="label">Vendor:</span><span class="value">${vendor?.business_name || 'N/A'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Payment Summary</div>
          ${subtotal > 0 ? `<div class="row"><span class="label">Subtotal:</span><span class="value">$${subtotal.toFixed(2)}</span></div>` : ''}
          ${platformFee > 0 ? `<div class="row"><span class="label">Service Charge</span><span class="value">$${platformFee.toFixed(2)}</span></div>` : ''}
          <div class="row"><span class="label">Total Amount:</span><span class="value">$${totalAmount.toFixed(2)}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Payment Transactions</div>
          ${completedPayments.map(payment => `
            <div class="payment-item">
              <div class="row"><span class="label">Payment Reference:</span><span class="value">${payment.payment_reference}</span></div>
              <div class="row"><span class="label">Type:</span><span class="value">${payment.payment_type === 'deposit' ? 'Deposit' : 'Full Payment'}</span></div>
              <div class="row"><span class="label">Amount:</span><span class="value">$${paymentCatalogAmount(payment).toFixed(2)}</span></div>
              <div class="row"><span class="label">Date:</span><span class="value">${formatDate(payment.created_at)}</span></div>
              <div class="row"><span class="label">Method:</span><span class="value">${payment.payment_method || 'Card'}</span></div>
            </div>
          `).join('')}
        </div>

        <div class="total">
          <div class="row">
            <span class="label">Total Paid:</span>
            <span class="value">$${totalPaid.toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          <p>Thank you for your business!</p>
          <p>This is an official receipt for your records.</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadReceipt = async () => {
    try {
      // Generate receipt HTML
      const receiptHTML = generateReceiptHTML();
      
      // Create a new window and print/download
      const printWindow = window.open('', '_blank');
      printWindow.document.write(receiptHTML);
      printWindow.document.close();
      
      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error generating receipt:', error);
      alert('Failed to generate receipt. Please try again later.');
    }
  };

  const completedPayments = payments.filter(p => p.status === 'completed');
  
  if (completedPayments.length === 0) {
    return null;
  }

  return (
    <button
      onClick={handleDownloadReceipt}
      className="flex items-center justify-center gap-2 w-full px-3 py-2 border border-black text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      Download Receipt
    </button>
  );
};

export default BookingReceipt;

